/**
 *  @class
 *  @function Pagination
 */

if (!customElements.get('pagination')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector('[data-id=' + id + ']');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.i = 2;
    }
    connectedCallback() {
      // Artiley 2026-10-23: listan lägger själv tillbaka läget vid bakåt, så webbläsarens egen
      // återställning stängs av redan här. Safari på iPhone har kända fel i den (WebKit bug 273617)
      // och kastar egna rättningar som hamnar i samma uppdatering.
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      this.loadId = Date.now() + '-' + Math.random().toString(36).slice(2);
      this.restorePosition();
      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }
      this.onGridClick = (e) => {
        const card = e.target.closest('.column');
        if (card) {
          this.clickedCard = card;
          // Spara redan vid klicket: Safari skickar inget pagehide om sidan inte hunnit ladda klart.
          this.savePosition();
        }
      };
      this.onPageHide = () => this.savePosition();
      this.onHidden = () => {
        if (document.visibilityState === 'hidden') this.savePosition();
      };
      this.onPageShow = (e) => {
        if (e.persisted) this.restoreFromMemory();
      };
      if (this.grid) this.grid.addEventListener('click', this.onGridClick, true);
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('pageshow', this.onPageShow);
      document.addEventListener('visibilitychange', this.onHidden);
    }
    disconnectedCallback() {
      if (this.grid) this.grid.removeEventListener('click', this.onGridClick, true);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('visibilitychange', this.onHidden);
    }
    // Artiley: listan kommer ihåg var kunden var. Webbläsaren sparar inte alltid sidan i
    // minnet till bakåt-knappen (mobil-Safari slänger den vid minnesbrist) och bygger
    // då om den med bara första sidan, så att kunden hamnar fel eller högst upp.
    storageKey() {
      return 'artiley-grid:' + location.pathname + location.search;
    }
    savePosition() {
      if (!this.grid) return;
      const cards = Array.from(this.grid.querySelectorAll('.column'));
      const card = this.clickedCard && this.grid.contains(this.clickedCard) ?
        this.clickedCard :
        cards.find(c => c.getBoundingClientRect().bottom > 0);
      if (!card) return;
      const link = card.querySelector('a[href*="/products/"]');
      // Läget finns också kvar i skriptets eget minne när sidan visas ur back/forward-cachen.
      this.lastSaved = {
        pages: this.i - 1,
        index: cards.indexOf(card),
        href: link ? link.getAttribute('href') : null,
        top: card.getBoundingClientRect().top,
        y: window.scrollY,
        id: this.loadId,
        t: Date.now()
      };
      try {
        sessionStorage.setItem(this.storageKey(), JSON.stringify(this.lastSaved));
        // Positionen är sparad: då återställer listan den själv, och webbläsarens egen
        // återställning (som drar mjukt mot fel ställe medan sidan växer) stängs av.
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      } catch (e) {}
    }
    restorePosition() {
      const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
      if (!nav || (nav.type !== 'back_forward' && nav.type !== 'reload') || !this.grid) return;
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(this.storageKey()));
      } catch (e) {}
      if (!saved) return;

      this.setAttribute('loading', true);
      const pageUrl = (page) => {
        const params = new URLSearchParams(location.search);
        params.set('page', page);
        return location.pathname + '?' + params.toString();
      };
      const pages = [];
      for (let page = 2; page <= saved.pages; page++) pages.push(page);
      Promise.all(pages.map(page => fetch(pageUrl(page)).then(response => response.text())))
        .then((htmls) => {
          htmls.forEach((html, n) => {
            const grid = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');
            if (!grid) return;
            grid.querySelectorAll('.column').forEach(card => this.grid.appendChild(card));
            dispatchCustomEvent('pagination:page-changed', {
              url: pageUrl(pages[n])
            });
          });
          this.i = saved.pages + 1;
        })
        .catch(() => {})
        .then(() => {
          this.settle(saved);
          this.doneLoading();
        });
    }
    savedTarget(saved) {
      const cards = Array.from(this.grid.querySelectorAll('.column'));
      const card = cards.find(c => {
        const link = c.querySelector('a[href*="/products/"]');
        return saved.href && link && link.getAttribute('href') === saved.href;
      }) || cards[saved.index];
      return card ? card.getBoundingClientRect().top + window.scrollY - saved.top : saved.y || 0;
    }
    scrollToSaved(saved) {
      // Temat har scroll-behavior: smooth på sidan; hoppet ska vara direkt.
      const root = document.documentElement,
        behavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, this.savedTarget(saved));
      root.style.scrollBehavior = behavior;
    }
    // Artiley 2026-10-23: sidan visas ur webbläsarens minne (back/forward-cachen) med alla tavlor kvar,
    // men Safari på iPhone lägger inte tillbaka scrollen när återställningen är manuell. Lägg tavlan på
    // plats och rätta några gånger medan Safari rör sidan; sluta så fort kunden själv rör skärmen.
    restoreFromMemory() {
      if (!this.grid) return;
      // Eget minne först; sessionStorage bara om det sparades under just den här sidvisningen, annars
      // kan ett gammalt läge från ett tidigare besök i samma flik användas.
      let saved = this.lastSaved || null;
      if (!saved) {
        try {
          saved = JSON.parse(sessionStorage.getItem(this.storageKey()));
        } catch (e) {}
        if (saved && saved.id !== this.loadId) saved = null;
      }
      if (saved) this.settle(saved);
    }
    settle(saved) {
      let stopped = false;
      const stop = () => {
        stopped = true;
      };
      ['touchstart', 'wheel', 'keydown', 'mousedown'].forEach(type => window.addEventListener(type, stop, {
        once: true,
        passive: true
      }));
      const step = (force) => {
        if (stopped) return;
        if (force || Math.abs(window.scrollY - this.savedTarget(saved)) > 2) this.scrollToSaved(saved);
      };
      step(false);
      // Första rättningen i nästa bildruta scrollar alltid: en scroll direkt i pageshow kan på iPhone
      // stanna inne i sidan utan att nå skärmen, och då visar scrollY rätt fast skärmen står på toppen.
      requestAnimationFrame(() => requestAnimationFrame(() => step(true)));
      [150, 400, 900, 1600].forEach(ms => setTimeout(() => step(false), ms));
    }
    addUrlParam(search, key) {
      var newParam = key + '=' + this.i,
        params = '?' + newParam;

      // If the "search" string exists, then build params from it
      if (search) {
        // Try to replace an existance instance
        params = search.replace(new RegExp('([?&])' + key + '[^&]*'), '$1' + newParam);

        // If nothing was replaced, then add the new param to the end
        if (params === search) {
          params += '&' + newParam;
        }
      }

      return params;
    }
    loadMore() {
      let base = this;
      this.button.addEventListener('click', function() {
        base.loadProducts();
        this.blur();
        return false;
      });
    }
    infinite() {
      let base = this;
      // Artiley: ladda när elementet närmar sig (600px innan det syns), inte först
      // när det är 100% synligt. Längst ner på sidan står det delvis utanför bild.
      base.observer = new IntersectionObserver(function(entries) {
        if (entries[0].isIntersecting) {
          base.loadProducts();
        }
      }, {
        rootMargin: '0px 0px 600px 0px'
      });
      base.observer.observe(base);
    }
    doneLoading() {
      this.removeAttribute('loading');
      // Artiley: observern rapporterar bara ändringar. Står elementet kvar i bild
      // när en sida laddats klart kommer ingen ny signal, så be om en ny avläsning.
      if (this.observer) {
        this.observer.unobserve(this);
        this.observer.observe(this);
      }
    }
    loadProducts() {
      let base = this,
        url = document.location.pathname + base.addUrlParam(document.location.search, 'page');
      if (base.getAttribute('loading')) {
        return;
      }
      base.i++;
      base.setAttribute('loading', true);
      fetch(url)
        .then(response => response.text())
        .then((responseText) => {
          const html = responseText;
          base.renderProducts(html, url);

          dispatchCustomEvent('pagination:page-changed', {
            url: url
          });
        })
        .catch(() => {
          // Artiley: nätverksfel ska inte låsa laddningen för gott.
          base.i--;
          base.removeAttribute('loading');
        });
    }
    renderProducts(html, url) {
      let base = this,
        grid_to_replace = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');

      if (!grid_to_replace) {
        if (base.observer) {
          base.observer.unobserve(base);
        }
        base.removeAttribute('loading');
        if (base.button) {
          base.button.classList.add('visually-hidden');
        }
        return;
      }
      let products = grid_to_replace.querySelectorAll('.column');

      for (var i = 0; i < products.length; i++) {
        this.grid.appendChild(products[i]);
      }
      if (this.animations_enabled) {
        gsap.set(products, {
          opacity: 0,
          y: 30
        });
        gsap.to(products, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05
        });
      }
      // Artiley: släpp spärren direkt när korten är på plats. Förut låg den kvar
      // under hela inblendningen (~2,9 s för 48 kort) och scroll under tiden ignorerades.
      base.doneLoading();
    }
  }
  customElements.define('pagination-theme', Pagination);
}